import { GROUP_LIFE_PRODUCT_CODE } from '@/app/config/products.config';
import { PmePolicyDetails } from '@/features/policies/components/pme/pme-policy-details.component';
import { PolicyDetails } from '@/features/policies/components/policy-details.component';
import { usePolicyDetails } from '@/features/policies/hooks/use-policy-details.hook';
import { requirePermissions } from '@/shared/utils';
import { Spinner } from '@noorden-insurtech/alba-design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * A aba ativa vive na URL para que a notificação de emissão possa levar o
 * corretor direto à Cobrança, onde está o link de pagamento. O `.catch` mantém
 * links antigos (sem `tab`) abrindo na aba Apólice em vez de quebrar a rota.
 */
const policyDetailsSearchSchema = z.object({
  tab: z.enum(['policy', 'billing']).optional().catch('policy'),
});

export const Route = createFileRoute('/_app/policies/$policyId/')({
  component: ViewPolicyRoute,
  validateSearch: policyDetailsSearchSchema,
  beforeLoad: async () => {
    const hasPermission = await requirePermissions(['policies.emissions.read']);
    if (!hasPermission) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function ViewPolicyRoute() {
  const { policyId } = Route.useParams();
  const { tab } = Route.useSearch();

  return <PolicyRouteResolver policyId={policyId} tab={tab} />;
}

/**
 * Escolhe a página de detalhe pelo produto da apólice — mesmo padrão de
 * `/analyses/$policyId`. Precisa acontecer aqui, e não na listagem, porque o
 * `PolicySummary` de `/policies` não expõe `productCode`; só o detalhe expõe.
 *
 * `usePolicyDetails` é o mesmo hook (e a mesma queryKey) que o `PolicyDetails`
 * usa por dentro, então o ramo genérico continua saindo do cache.
 *
 * A aba (`tab`) só vale para o detalhe genérico: a tela do PME não tem
 * Cobrança.
 */
function PolicyRouteResolver({
  policyId,
  tab,
}: {
  policyId: string;
  tab?: 'policy' | 'billing';
}) {
  const { data: policy, isLoading } = usePolicyDetails(policyId);

  if (isLoading) {
    return (
      <div className='flex justify-center py-24'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  if (policy?.productCode === GROUP_LIFE_PRODUCT_CODE) {
    return <PmePolicyDetails policyId={policyId} policy={policy} />;
  }

  return <PolicyDetails policyId={policyId} tab={tab} />;
}
